<template>
  <PageEntityHeader
    :banner-url="bannerUrl || coverUrl"
    :blur="!bannerUrl"
    :paths="paths"
    v-model:current-tab="current"
  >
    <div
      class="relative h-52 sm:h-80 -mt-24 sm:-mt-40 z-10 grid justify-center sm:justify-start space-x-4 sm:grid-cols-[calc(20rem/7*5)_1fr]"
    >
      <TitleCover
        class="min-h-0 h-48 sm:h-80 rounded-md ring-4 ring-black dark:ring-white"
        :title
        res="512"
      />
      <div class="hidden sm:grid grid-rows-[10rem_1fr_auto] min-h-0">
        <div></div>
        <Skeleton v-if="showLoaders" class="mt-2 h-12 w-48" />
        <div v-else class="flex min-h-0 overflow-hidden">
          <BigText
            :text="titleTitle"
            style="word-break: break-word"
            class="mt-2 xl:mt-4 min-h-0 text-2xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold"
          />
        </div>
        <ul class="flex text-base space-x-4 justify-start">
          <li
            class="whitespace-nowrap flex items-center space-x-2 shadow input-border rounded-full py-0.5 px-2"
          >
            <IconBuilding
              class="flex-shrink-0 text-nami-nami-blue"
              :size="18"
            />
            <Skeleton v-if="showLoaders" class="h-4 w-48 my-auto shadow-none" />
            <TheNuxtLink
              v-else
              v-for="org in organizations"
              :to="linkTo('org', org.attributes!.slug)"
            >
              {{ org.attributes?.name }}
            </TheNuxtLink>
          </li>
          <TitleStats :titleId @comment="navigateTo(href + '/comments')" />
          <li class="my-auto">
            <TheNuxtLink
              :to="href + '/reviews'"
              class="whitespace-nowrap flex items-center space-x-2 my-auto select-none"
            >
              <Skeleton v-if="ratingsPending" class="h-3/4 w-16" />
              <template v-else-if="ratings">
                <ReviewDisplay :rating="ratings.attributes.rating" />
                <span class="text-sm opacity-70 mt-1">
                  ({{ ratings.attributes.rating.toFixed(1) }})
                </span>
              </template>
            </TheNuxtLink>
          </li>
        </ul>
      </div>
    </div>
    <div class="sm:hidden mt-0 text-xl font-bold text-center px-2">
      {{ titleTitle }}
    </div>
    <div class="mt-2 sm:mt-4 space-y-2">
      <div
        v-if="titleLoading"
        class="container grid sm:grid-cols-[calc(20rem/7*5)_1fr] gap-2 sm:gap-4 h-10"
      >
        <Skeleton />
        <div class="flex space-x-4">
          <Skeleton class="w-32" />
          <Skeleton class="w-24" />
          <Skeleton class="w-24" />
          <Skeleton class="w-10" />
        </div>
      </div>
      <div
        v-else
        class="container flex flex-col-reverse items-center sm:flex-row sm:space-x-4"
      >
        <div
          class="flex flex-wrap justify-center sm:grid sm:grid-cols-[calc(20rem/7*5)_1fr] gap-2 sm:gap-4"
        >
          <TitleFollowButton
            :title-id="titleId"
            class="dynamic-width"
            ref="followButton"
          />
          <div
            class="flex items-center gap-2 sm:gap-4 justify-center sm:justify-start flex-wrap sm:flex-nowrap sm:overflow-x-auto"
          >
            <NamiButton
              v-if="title"
              :href="`${orgLink}/subscriptions`"
              buttonType="primary"
              :icon="IconHeart"
            >
              Support
            </NamiButton>
            <TitleListAddEditButton :titleId="titleId" />
            <NamiButton
              @buttonClick="
                () => {
                  if (!title) {
                    return;
                  }

                  appStore?.prompt(
                    ModalTitleShare,
                    { large: true },
                    {
                      title: title,
                    },
                  );
                }
              "
              buttonType="primary"
              :icon="IconShare"
              text
            >
              {{ $t("components.titleHeader.buttonShareText") }}
            </NamiButton>
            <NamiButton
              v-if="title"
              class="self-stretch sm:!flex !hidden whitespace-nowrap"
              button-type="primary"
              text
              :href="`${linkTo(
                'title',
                title.id,
                title.attributes.slug,
              )}/reviews`"
            >
              Write a review
            </NamiButton>
            <div
              @click="
                () => {
                  if (!title) {
                    return;
                  }

                  appStore?.prompt(
                    ModalTitleShare,
                    { large: true },
                    {
                      title: title,
                    },
                  );
                }
              "
              class="sm:hidden absolute z-10 top-[4.25em] right-14 p-2 cursor-pointer"
            >
              <IconShare
                class="p-[0.375rem] bg-black/70 text-white transition-opacity rounded-full"
                :size="28"
              />
            </div>
            <ContextMenuList
              class="sm:hidden absolute z-10 top-[4.25rem] right-1"
              :options="[
                {
                  name: t('site.title.id.slug.buttonReport'),
                  action: () => {
                    if (!isLoggedIn) appStore?.openLoginRequiredModal();
                    else showReportModal = true;
                  },
                  icon: IconFlag,
                },
              ]"
            >
              <template v-slot="{ isOpen, open, close }">
                <div
                  @click="isOpen ? close() : open()"
                  class="p-2 cursor-pointer"
                >
                  <IconDots
                    class="p-[0.375rem] bg-black/70 text-white transition-opacity rounded-full"
                    :class="{
                      'opacity-50': isOpen,
                    }"
                    :size="28"
                  />
                </div>
              </template>
            </ContextMenuList>
            <ContextMenuList
              closeOnOutsideClick
              defaultPosition="bottom-left"
              class="hidden sm:inline-block relative"
              :options="[
                {
                  name: t('site.title.id.slug.buttonReport'),
                  action: () => {
                    if (!isLoggedIn) appStore?.openLoginRequiredModal();
                    else showReportModal = true;
                  },
                  icon: IconFlag,
                },
              ]"
            >
              <template v-slot="{ isOpen, open, close }">
                <NamiButton
                  @buttonClick="() => (isOpen ? close() : open())"
                  buttonType="secondary"
                  :icon="IconDots"
                  :text="!isOpen"
                  iconPosition="right"
                />
              </template>
            </ContextMenuList>
            <div class="hidden sm:block">
              <slot name="after-options"></slot>
            </div>
          </div>
        </div>
        <TitleStats
          class="mt-0 mb-6 sm:hidden"
          :titleId
          small
          @comment="navigateTo(href + '/comments')"
        />
        <TheNuxtLink
          :to="href + '/reviews'"
          class="sm:hidden whitespace-nowrap flex items-center space-x-2 mb-2 select-none"
        >
          <Skeleton v-if="ratingsPending" class="h-3/4 w-16" />
          <template v-else-if="ratings">
            <ReviewDisplay :rating="ratings.attributes.rating" />
            <span class="text-sm opacity-70 mt-1">
              ({{ ratings.attributes.rating.toFixed(1) }})
            </span>
          </template>
        </TheNuxtLink>
        <div
          class="mb-2 py-1 px-3 rounded-full dark:bg-neutral-800 bg-white sm:hidden whitespace-nowrap flex items-center space-x-1"
        >
          <IconBuilding class="flex-shrink-0 text-nami-nami-blue" :size="16" />
          <TheNuxtLink
            v-for="org in organizations"
            :to="linkTo('org', org.attributes!.slug)"
            class="px-1 text-sm font-medium transition-colors dark:hover:bg-neutral-500/50"
          >
            {{ org.attributes?.name }}
          </TheNuxtLink>
        </div>
      </div>
    </div>

    <TheTitleInfo class="mt-4" v-if="title" :titleEntity="title" />

    <ReportModal
      v-model="showReportModal"
      entityType="title"
      :entityId="titleId"
    />
  </PageEntityHeader>
</template>

<script setup lang="ts">
import {
  Title,
  Rating,
  getRelationship,
  type TitleEntity,
  Statistics,
} from "~/src/api";
import BigText from "~/components/BigText.vue";
import {
  IconBuilding,
  IconDots,
  IconShare,
  IconHeart,
  IconFlag,
} from "@tabler/icons-vue";
import AsyncImage from "~/components/async/AsyncImage.vue";
import { parseTitleForDisplay } from "~/utils/display/title";
import ModalTitleShare from "../../modal/title/Share.vue";
import { TitleFollowButton } from "#components";
import type { PageTab } from "~/types/navigation";
import { useQuery } from "@tanstack/vue-query";

const hrefMap = useHref();
const nuxtApp = useNuxtApp();
const appStore = nuxtApp.$app();
const authStore = nuxtApp.$auth();
const { t } = useI18n();

type Props = {
  title?: TitleEntity;
  titleId: string;
  slug: string;
  titleLoading?: boolean;
};

const { titleId, title, slug, titleLoading } = defineProps<Props>();

const { current, paths } = useSubpaths(
  computed(() => ({
    rootPath: hrefMap.value.title.id(titleId).slug(slug).index,
    paths: [
      {
        value: "/",
        name: t(
          `site.title.id.slug.tab.chapters.${
            title?.attributes.chapterKind ?? "chapter"
          }`,
        ),
      },
      { value: "/details", name: t(`site.title.id.slug.tab.details`) },
      { value: "/comments", name: t(`site.title.id.slug.tab.comments`) },
      { value: "/art", name: t(`site.title.id.slug.tab.art`) },
      // { value: "/collections", name: t(`site.title.id.slug.tab.collections`) },
      { value: "/reviews", name: t(`site.title.id.slug.tab.reviews`) },
    ],
    default: "/",
  })),
);

const followButton = ref<InstanceType<typeof TitleFollowButton>>();

const isLoggedIn = computed(() => !!authStore?.user);

const showLoaders = computed(() => titleLoading || !title);

const showReportModal = ref(false);

const orgLink = computed(() => {
  if (!title) return "/";

  const { id, attributes } = getRelationship(title, "organization", true);

  return linkTo("org", id, attributes.slug);
});

const {
  title: titleTitle,
  organizations,
  coverUrl,
  bannerUrl,
  href,
} = parseTitleForDisplay(
  computed(() => title),
  "large",
);

const {
  data: stats,
  isPending: statsPending,
  suspense: statPrefetch,
} = useQuery({
  queryKey: ["composite_stats", "entity", "title", titleId],
  queryFn: async () => {
    return await Title.getStats(titleId);
  },
  staleTime: useCacheTimings().entity.composite_stats,
});

const {
  data: ratings,
  isPending: ratingsPending,
  suspense: ratingPrefetch,
} = useQuery({
  queryKey: ["title_rating", "entity", titleId],
  queryFn: async () => {
    return await Rating.getTitleRatings(titleId);
  },
  staleTime: useCacheTimings().entity.title_rating,
});

onServerPrefetch(async () => {
  await Promise.all([statPrefetch(), ratingPrefetch()]);
});

onMounted(() => {
  authStore?.waitUntilRefreshIsComplete().then(() => {
    followButton.value?.initialize();
  });
});
</script>
